import React, { useEffect, useId, useRef, useState } from 'react';
import { adminApi, errorMessage } from '../api';
import { Dialog, EsFehlt, Field, FormError, StaleConflict } from '../components';
import { Segmente } from '../../pages/einstellungen/Bausteine';
import { PERSONEN } from './cockpitTexte';
import { FirmaEinstellungen, firmaAdresse } from './cockpitApi';

// Profil-Nachbau F-08 (output/profil-team-20261014/BAU-COCKPIT.md 1.3.1, 3.3; Auftrag PF-506-2): Namen im KI-Cockpit der Firma.
// Einschalten nur der Besitzer (darfEinschalten), mit Grundlage, Bestätigung und Passwort; ausschalten der Besitzer oder wer
// die Firma ändern darf (darfAusschalten), ohne Dialog. Beides entscheidet der Server (cockpit/firma.ts, firmaRouter.ts: Recht
// vor Passwort) und schreibt es ins Änderungsprotokoll; diese Datei zeigt nur, was die Einstellungen erlauben. Das Passwort
// lebt nur im Zustand des Dialogs und geht nur an den Server.

type Grundlage = 'BETRIEBSVEREINBARUNG' | 'ZUSTIMMUNG';
const GRUNDLAGEN: Grundlage[] = ['BETRIEBSVEREINBARUNG', 'ZUSTIMMUNG'];
const fehlerCode = (error: unknown) => (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;

/** Knopf „Namen zeigen …“ bzw. „Namen ausblenden“ je nach Einstellung und Recht, sonst nichts. onGeaendert lädt Einstellungen
 *  und Personen neu und nennt die Meldung (ohne Meldung nach „Neu laden“). fokus: der Knopf bekommt beim Erscheinen den Fokus
 *  (nach einem Wechsel steht dort der andere Knopf). */
export default function NamenSchalter({ organizationId, einstellungen, onGeaendert, fokus = false, ohneAnzahlen = false }: { organizationId: string;
  einstellungen: FirmaEinstellungen; onGeaendert: (meldung?: string) => void; fokus?: boolean; ohneAnzahlen?: boolean }) {
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (fokus) knopf.current?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const weg = firmaAdresse(organizationId, 'einstellungen');

  if (!einstellungen.namenSichtbar && einstellungen.darfEinschalten) return <>
    <button ref={knopf} type="button" className="adm-button" onClick={() => setOffen(true)}>{PERSONEN.namenZeigen}</button>
    {offen && <EinschaltDialog weg={weg} revision={einstellungen.revision} ohneAnzahlen={ohneAnzahlen} onClose={() => setOffen(false)}
      onEingeschaltet={() => { setOffen(false); onGeaendert(PERSONEN.eingeschaltet); }} onNeuLaden={() => { setOffen(false); onGeaendert(); }} />}
  </>;

  if (einstellungen.namenSichtbar && einstellungen.darfAusschalten) {
    const ausschalten = async () => {
      setBusy(true); setFehler(null);
      try {
        await adminApi.put(weg, { expectedRevision: einstellungen.revision, namenSichtbar: false });
        onGeaendert(PERSONEN.ausgeblendet);
      } catch (error) { setFehler(error); setBusy(false); }
    };
    return <>
      <button ref={knopf} type="button" className="adm-button" disabled={busy} aria-busy={busy || undefined} onClick={() => void ausschalten()}>{PERSONEN.namenAusblenden}</button>
      <FormError message={fehler ? errorMessage(fehler) : undefined} />
      <StaleConflict error={fehler} onReload={() => { setFehler(null); onGeaendert(); }} />
    </>;
  }
  return null;
}

// Dialog „Namen im KI-Cockpit zeigen?“: gelber Satz vor dem Klick, Grundlage, Bestätigung und Passwort, alle Pflicht. Der Knopf
// ist gesperrt, solange etwas fehlt, und „Es fehlt noch: …“ nennt es. Ein falsches Passwort steht am Feld, alles andere mit dem
// Satz des Servers; 409 bietet „Neu laden“. Mit Eingaben fragen Escape und „Dialog schließen“ nach.
function EinschaltDialog({ weg, revision, onClose, onEingeschaltet, onNeuLaden, ohneAnzahlen }: { weg: string; revision: number; ohneAnzahlen: boolean; onClose: () => void;
  onEingeschaltet: () => void; onNeuLaden: () => void }) {
  const [grundlage, setGrundlage] = useState<Grundlage | ''>('');
  const [bestaetigt, setBestaetigt] = useState(false);
  const [passwort, setPasswort] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const grundlageId = useId();
  const fehlt = [...(grundlage ? [] : [PERSONEN.esFehlt.grundlage]), ...(bestaetigt ? [] : [PERSONEN.esFehlt.bestaetigung]),
    ...(passwort ? [] : [PERSONEN.esFehlt.passwort])];
  const passwortFalsch = fehlerCode(fehler) === 'OPERATOR_PASSWORD_INVALID';

  const senden = async (event: React.FormEvent) => {
    event.preventDefault();
    if (fehlt.length || !grundlage) return;
    setBusy(true); setFehler(null);
    try {
      await adminApi.put(weg, { expectedRevision: revision, namenSichtbar: true, grundlage, bestaetigt: true, password: passwort });
      setPasswort('');
      onEingeschaltet();
    } catch (error) { setPasswort(''); setFehler(error); setBusy(false); }
  };

  return <Dialog title={PERSONEN.dialogTitel} onClose={onClose} busy={busy} verwerfenFragen={!!grundlage || bestaetigt || !!passwort}>
    <form onSubmit={senden} aria-busy={busy} noValidate>
      <p className="adm-notice" data-vor={PERSONEN.namenZeigenKnopf}>{ohneAnzahlen ? PERSONEN.dialogWarnung.replace('Recherchen und Anteil', 'Anteil') : PERSONEN.dialogWarnung}</p>
      <div className="adm-field">
        <span id={grundlageId}>{PERSONEN.grundlage}</span>
        <Segmente name={`${grundlageId}-wahl`} label={PERSONEN.grundlage} beschriftungId={grundlageId} wert={grundlage} disabled={busy}
          optionen={GRUNDLAGEN.map(wert => ({ wert, text: PERSONEN.grundlagen[wert] }))} onWahl={wert => { setGrundlage(wert); setFehler(null); }} />
      </div>
      <label className="adm-check"><input type="checkbox" checked={bestaetigt} aria-required="true" disabled={busy}
        onChange={event => { setBestaetigt(event.target.checked); setFehler(null); }} />{PERSONEN.bestaetigung}</label>
      <Field label={PERSONEN.passwort} fehler={passwortFalsch ? errorMessage(fehler) : undefined}>
        <input type="password" autoComplete="current-password" aria-required="true" value={passwort} disabled={busy}
          onChange={event => setPasswort(event.target.value)} /></Field>
      {!passwortFalsch && <FormError message={fehler ? errorMessage(fehler) : undefined} />}
      <StaleConflict error={fehler} onReload={onNeuLaden} />
      <footer>
        <button type="button" className="adm-button" disabled={busy} onClick={onClose}>{PERSONEN.abbrechen}</button>
        <button type="submit" className="adm-button adm-primary" disabled={busy || fehlt.length > 0}>{PERSONEN.namenZeigenKnopf}</button>
      </footer>
      <EsFehlt teile={fehlt} />
    </form>
  </Dialog>;
}
